import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionEnum, TransactionComments } from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetProblemTransactionCommentsQuery,
  useAddCommentToProblemTransactionMutation,
  useEditCommentInProblemTransactionMutation,
  useDeleteCommentInProblemTransactionMutation,
} from '../store/services/customers';

const InternalTransactionComments = (): React.JSX.Element => {
  const {
    customersProblemTransactions: {
      internal: { transactionComments, transactionCommentsPagination },
    },
    customersDictionaries: { activePermissions },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const { transactionId } = useParams();

  const [
    getTransactionComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetProblemTransactionCommentsQuery();
  const [
    addCommentToTransaction,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToProblemTransactionMutation();
  const [
    editCommentInTransaction,
    {
      isLoading: editTransactionCommentInProgress,
      isSuccess: editTrancastionCommentIsSuccess,
    },
  ] = useEditCommentInProblemTransactionMutation();
  const [
    deleteCommentInTransaction,
    {
      isLoading: deleteCommentInTransactionInProgress,
      isSuccess: deleteCommentInTranscationIsSuccess,
    },
  ] = useDeleteCommentInProblemTransactionMutation();

  const onAddCommentToTransaction = async (text: string) => {
    await addCommentToTransaction({
      id: transactionId as string,
      url: `transactions-internal`,
      body: {
        text,
      },
    });
  };

  const onEditCommentInTransaction = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInTransaction({
      id: transactionId as string,
      url: `transactions-internal`,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInTransaction = async (commentId: string) => {
    await deleteCommentInTransaction({
      id: transactionId as string,
      url: `transactions-internal`,
      commentId,
    });
  };

  const getTransactionCommentsAction = async (cursor?: string) => {
    await getTransactionComments({
      id: transactionId as string,
      url: `transactions-internal`,
      cursor: cursor || '',
    });
  };

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_LIST,
      )
    ) {
      getTransactionCommentsAction();
    }
  }, [activePermissions]);

  return (
    <TransactionComments
      comments={transactionComments}
      pagination={transactionCommentsPagination}
      isLoading={commentsIsLoading}
      isFetching={commentsIsFetching}
      commentsAvailable={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_LIST,
      )}
      getCommentsList={getTransactionCommentsAction}
      createAvailable={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_CREATE_ACTION,
      )}
      editAvailable={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_EDIT_ACTION,
      )}
      deleteAvailable={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_INTERNAL_COMMENTS_DELETE_ACTION,
      )}
      onAddCommentToTransaction={onAddCommentToTransaction}
      addCommentInProgress={addCommentInProgress}
      addCommentIsSuccess={addCommentIsSuccess}
      onEditCommentInTransaction={onEditCommentInTransaction}
      editTransactionCommentInProgress={editTransactionCommentInProgress}
      editTrancastionCommentIsSuccess={editTrancastionCommentIsSuccess}
      users={users}
      onDeleteCommentInTransaction={onDeleteCommentInTransaction}
      deleteCommentInTransactionInProgress={
        deleteCommentInTransactionInProgress
      }
      deleteCommentInTranscationIsSuccess={deleteCommentInTranscationIsSuccess}
      changeLogsAvailable={activePermissions?.includes(
        PermissionEnum.AUDIT_LOG_LIST,
      )}
    />
  );
};

export default InternalTransactionComments;
